import {
  cancelFrame,
  requestFrame
} from '../render/render_scheduler.js?v=20260706e';

const controllers = new Map();
const renderPassStack = [];

let renderPassSequence = 0;
let viewportRevision = 0;
let pendingViewportFrame = 0;
let pendingViewportState = null;

const developmentRuntime = (() => {
  if (import.meta.env?.DEV === true) return true;
  const host = String(globalThis?.location?.hostname || '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
})();

let diagnosticsEnabled = false;
let longTaskObserver = null;
let lastCullingFrameAt = 0;
let diagnosticFrameHandle = 0;
const diagnosticState = {
  assets: new Map(),
  updates: [],
  frames: [],
  longTasks: []
};

function nowMs() {
  return Number(globalThis.performance?.now?.()) || Date.now();
}

function currentImportedSvgRenderPass() {
  return renderPassStack[renderPassStack.length - 1] || null;
}

function beginImportedSvgRenderPass(options = {}) {
  const pass = {
    id: ++renderPassSequence,
    scope: String(options.scope || 'plan'),
    isolated: options.isolated === true,
    forceFull: options.forceFull === true,
    sweep: options.sweep !== false,
    seenKeys: new Set()
  };
  if (pass.forceFull && options.isolated !== true) cancelImportedSvgViewportUpdate();
  renderPassStack.push(pass);
  return pass;
}

function endImportedSvgRenderPass(pass) {
  const current = currentImportedSvgRenderPass();
  if (!pass || current !== pass) {
    const index = renderPassStack.lastIndexOf(pass);
    if (index >= 0) renderPassStack.splice(index, 1);
    return false;
  }
  renderPassStack.pop();
  for (const [key, controller] of controllers) {
    const missingFromCompleteScope = pass.sweep
      && controller.scope === pass.scope
      && !pass.seenKeys.has(key);
    const staleDetachedScope = !pass.isolated
      && controller.scope !== pass.scope
      && controller.host
      && controller.host.isConnected === false;
    if (!missingFromCompleteScope && !staleDetachedScope) continue;
    controller.dispose?.();
    controllers.delete(key);
  }
  return true;
}

function importedSvgRenderForcesFullDom() {
  return currentImportedSvgRenderPass()?.forceFull === true;
}

function shouldForceFullImportedSvgDom(options = {}) {
  return options.printCapture === true
    || (options.bomCapture === true && options.bomPlanRender === true);
}

function importedSvgRuntimeController(key) {
  return controllers.get(String(key || '')) || null;
}

function registerImportedSvgRuntimeController(key, controller, options = {}) {
  const normalizedKey = String(key || '');
  if (!normalizedKey || !controller) return null;
  const previous = controllers.get(normalizedKey);
  if (previous && previous !== controller) previous.dispose?.();
  controller.scope = String(options.scope || controller.scope || currentImportedSvgRenderPass()?.scope || 'plan');
  controllers.set(normalizedKey, controller);
  currentImportedSvgRenderPass()?.seenKeys.add(normalizedKey);
  return controller;
}

function touchImportedSvgRuntimeController(key) {
  const normalizedKey = String(key || '');
  if (!normalizedKey) return false;
  currentImportedSvgRenderPass()?.seenKeys.add(normalizedKey);
  return controllers.has(normalizedKey);
}

function removeImportedSvgRuntimeController(key) {
  const normalizedKey = String(key || '');
  const controller = controllers.get(normalizedKey);
  if (!controller) return false;
  controller.dispose?.();
  controllers.delete(normalizedKey);
  return true;
}

function runImportedSvgViewportUpdate(state, revision) {
  if (!state || revision !== viewportRevision) return false;
  const startedAt = nowMs();
  let mounts = 0;
  let unmounts = 0;
  let updatedControllers = 0;
  let mountedUnits = 0;
  for (const controller of controllers.values()) {
    if (state.scope && controller.scope !== state.scope) continue;
    if (!controller.host?.isConnected) continue;
    const result = controller.updateViewport?.(state, { revision }) || null;
    if (!result) continue;
    updatedControllers += 1;
    mounts += Number(result.mounts) || 0;
    unmounts += Number(result.unmounts) || 0;
    mountedUnits += Number(result.mountedUnits) || 0;
  }
  const durationMs = nowMs() - startedAt;
  if (diagnosticsEnabled) {
    diagnosticState.updates.push({
      revision,
      reason: state.reason || 'viewport',
      durationMs,
      mounts,
      unmounts,
      mountedUnits,
      controllers: updatedControllers,
      at: Date.now()
    });
    trimDiagnosticEntries(diagnosticState.frames, 600);
    trimDiagnosticEntries(diagnosticState.updates, 600);
  }
  return true;
}

function sampleImportedSvgDiagnosticFrame() {
  if (!diagnosticsEnabled) {
    diagnosticFrameHandle = 0;
    return;
  }
  const frameAt = nowMs();
  if (lastCullingFrameAt) diagnosticState.frames.push(frameAt - lastCullingFrameAt);
  lastCullingFrameAt = frameAt;
  trimDiagnosticEntries(diagnosticState.frames, 600);
  diagnosticFrameHandle = requestFrame(sampleImportedSvgDiagnosticFrame);
}

function importedSvgDiagnosticsEnabled() {
  return developmentRuntime && diagnosticsEnabled;
}

function scheduleImportedSvgViewportUpdate(state = {}, options = {}) {
  pendingViewportState = { ...state };
  const revision = ++viewportRevision;
  if (options.immediate === true) {
    if (pendingViewportFrame) cancelFrame(pendingViewportFrame);
    pendingViewportFrame = 0;
    const current = pendingViewportState;
    pendingViewportState = null;
    return runImportedSvgViewportUpdate(current, revision);
  }
  if (pendingViewportFrame) return false;
  pendingViewportFrame = requestFrame(() => {
    pendingViewportFrame = 0;
    const current = pendingViewportState;
    pendingViewportState = null;
    runImportedSvgViewportUpdate(current, viewportRevision);
  });
  return true;
}

function cancelImportedSvgViewportUpdate() {
  viewportRevision += 1;
  pendingViewportState = null;
  if (!pendingViewportFrame) return false;
  cancelFrame(pendingViewportFrame);
  pendingViewportFrame = 0;
  return true;
}

function recordImportedSvgAssetDiagnostics(key, values = {}) {
  if (!diagnosticsEnabled) return;
  diagnosticState.assets.set(String(key || `asset-${diagnosticState.assets.size + 1}`), {
    ...(diagnosticState.assets.get(String(key || '')) || {}),
    ...values
  });
}

function trimDiagnosticEntries(entries, limit) {
  if (entries.length > limit) entries.splice(0, entries.length - limit);
}

function percentile(values, ratio) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * ratio)))] || 0;
}

function rounded(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function importedSvgDiagnosticsSummary() {
  const assets = [...diagnosticState.assets.values()];
  const updates = diagnosticState.updates;
  const updateDurations = updates.map(entry => entry.durationMs);
  const frameDurations = diagnosticState.frames;
  const controllerRows = [...controllers.entries()].map(([key, controller]) => ({
    key,
    totalUnits: Number(controller.totalUnits) || 0,
    mountedUnits: Number(controller.mountedIds?.length ?? controller.mountedIds?.size) || 0,
    connected: controller.host?.isConnected === true
  }));
  return {
    enabled: diagnosticsEnabled,
    assets: {
      count: assets.length,
      totalDomNodes: assets.reduce((sum, entry) => sum + (Number(entry.totalDomNodes) || 0), 0),
      drawingElements: assets.reduce((sum, entry) => sum + (Number(entry.drawingElements) || 0), 0),
      parsingMs: rounded(assets.reduce((sum, entry) => sum + (Number(entry.parsingMs) || 0), 0)),
      boundsMs: rounded(assets.reduce((sum, entry) => sum + (Number(entry.boundsMs) || 0), 0)),
      indexMs: rounded(assets.reduce((sum, entry) => sum + (Number(entry.indexMs) || 0), 0)),
      firstVisibleMs: rounded(assets.reduce((sum, entry) => sum + (Number(entry.firstVisibleMs) || 0), 0))
    },
    viewport: {
      updateCount: updates.length,
      averageMs: rounded(updateDurations.reduce((sum, value) => sum + value, 0) / Math.max(1, updateDurations.length)),
      p95Ms: rounded(percentile(updateDurations, 0.95)),
      maxMs: rounded(Math.max(0, ...updateDurations)),
      mounts: updates.reduce((sum, entry) => sum + entry.mounts, 0),
      unmounts: updates.reduce((sum, entry) => sum + entry.unmounts, 0),
      mountedUnits: controllerRows.reduce((sum, entry) => sum + entry.mountedUnits, 0)
    },
    frames: {
      sampleCount: frameDurations.length,
      averageMs: rounded(frameDurations.reduce((sum, value) => sum + value, 0) / Math.max(1, frameDurations.length)),
      p95Ms: rounded(percentile(frameDurations, 0.95)),
      over50ms: frameDurations.filter(value => value > 50).length
    },
    longTasks: {
      count: diagnosticState.longTasks.length,
      maxMs: rounded(Math.max(0, ...diagnosticState.longTasks.map(entry => entry.duration)))
    },
    memory: Number(globalThis.performance?.memory?.usedJSHeapSize) || null,
    controllers: controllerRows
  };
}

function clearImportedSvgDiagnostics() {
  diagnosticState.assets.clear();
  diagnosticState.updates.length = 0;
  diagnosticState.frames.length = 0;
  diagnosticState.longTasks.length = 0;
  lastCullingFrameAt = 0;
}

function setImportedSvgDiagnosticsEnabled(value) {
  if (!developmentRuntime) return false;
  diagnosticsEnabled = value === true;
  if (diagnosticsEnabled && !diagnosticFrameHandle) {
    lastCullingFrameAt = 0;
    diagnosticFrameHandle = requestFrame(sampleImportedSvgDiagnosticFrame);
  }
  if (diagnosticsEnabled && !longTaskObserver && typeof PerformanceObserver === 'function') {
    try {
      longTaskObserver = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          diagnosticState.longTasks.push({ duration: entry.duration, startTime: entry.startTime });
        }
        trimDiagnosticEntries(diagnosticState.longTasks, 300);
      });
      longTaskObserver.observe({ type: 'longtask', buffered: true });
    } catch (_error) {
      longTaskObserver = null;
    }
  }
  if (!diagnosticsEnabled && longTaskObserver) {
    longTaskObserver.disconnect();
    longTaskObserver = null;
  }
  if (!diagnosticsEnabled && diagnosticFrameHandle) {
    cancelFrame(diagnosticFrameHandle);
    diagnosticFrameHandle = 0;
    lastCullingFrameAt = 0;
  }
  return diagnosticsEnabled;
}

export {
  beginImportedSvgRenderPass,
  cancelImportedSvgViewportUpdate,
  clearImportedSvgDiagnostics,
  currentImportedSvgRenderPass,
  endImportedSvgRenderPass,
  importedSvgDiagnosticsSummary,
  importedSvgDiagnosticsEnabled,
  importedSvgRenderForcesFullDom,
  importedSvgRuntimeController,
  recordImportedSvgAssetDiagnostics,
  registerImportedSvgRuntimeController,
  removeImportedSvgRuntimeController,
  scheduleImportedSvgViewportUpdate,
  setImportedSvgDiagnosticsEnabled,
  shouldForceFullImportedSvgDom,
  touchImportedSvgRuntimeController
};

// Release only the temporary output pass; live plan controllers stay owned by the viewport.
export function releaseImportedSvgCaptureControllers() {
  for (const [key, controller] of controllers) {
    if (controller.scope !== 'capture') continue;
    try { controller.dispose?.(); } finally { controllers.delete(key); }
  }
}
